import { useState, useEffect, useRef, useCallback } from 'react';

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

interface DraggableResizableOptions {
  defaultSize?: Size;
  minSize?: Size;
  storageKey?: string;
  initialPosition?: 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export const useDraggableResizable = (options: DraggableResizableOptions = {}) => {
  const {
    defaultSize = { width: 1100, height: 320 },
    minSize = { width: 600, height: 260 },
    storageKey,
    initialPosition = 'bottom-right',
  } = options;

  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeEdge, setResizeEdge] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const [, setResizeOffset] = useState<Position>({ x: 0, y: 0 });
  const resizeStartRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
    posX: number;
    posY: number;
  } | null>(null);

  // Function to get the starting position
  const getInitialPosition = (position: string, width: number, height: number): Position => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 24;

    switch (position) {
      case 'center':
        return {
          x: (viewportWidth - width) / 2,
          y: (viewportHeight - height) / 2,
        };
      case 'top-left':
        return { x: padding, y: padding };
      case 'top-right':
        return { x: viewportWidth - width - padding, y: padding };
      case 'bottom-right':
        return {
          x: viewportWidth - width - padding,
          y: viewportHeight - height - padding,
        };
      case 'bottom-left':
      default:
        return {
          x: padding,
          y: viewportHeight - height - padding,
        };
    }
  };

  // Function to constrain position within viewport
  const constrainPosition = (x: number, y: number, width: number, height: number): Position => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    return {
      x: Math.max(0, Math.min(x, viewportWidth - width)),
      y: Math.max(0, Math.min(y, viewportHeight - height)),
    };
  };

  const loadFromStorage = (): { position: Position; size: Size } | null => {
    if (!storageKey) return null;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.position && parsed.size) {
          return {
            position: parsed.position,
            size: parsed.size,
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load draggable state from localStorage:', error);
    }
    return null;
  };

  const saveToStorage = useCallback(
    (position: Position, size: Size) => {
      if (!storageKey) return;

      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            position,
            size,
            timestamp: Date.now(),
          }),
        );
      } catch (error) {
        console.warn('Failed to save draggable state to localStorage:', error);
      }
    },
    [storageKey],
  );

  // Initializing position and size
  const initializeState = () => {
    const saved = loadFromStorage();

    if (saved) {
      // Check if the saved position is still within the viewport
      const constrainedPosition = constrainPosition(
        saved.position.x,
        saved.position.y,
        saved.size.width,
        saved.size.height,
      );

      return {
        position: constrainedPosition,
        size: {
          width: Math.max(saved.size.width, minSize.width),
          height: Math.max(saved.size.height, minSize.height),
        },
      };
    }

    // Used the default values
    const initialPos = getInitialPosition(initialPosition, defaultSize.width, defaultSize.height);
    return {
      position: initialPos,
      size: defaultSize,
    };
  };

  const [position, setPosition] = useState<Position>(() => initializeState().position);
  const [size, setSize] = useState<Size>(() => initializeState().size);

  // Saving state when changing position or size
  useEffect(() => {
    saveToStorage(position, size);
  }, [position, saveToStorage, size]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isInteractive =
      target.tagName !== 'DIV' ||
      target.isContentEditable ||
      target.closest('input, textarea, select, button, a, [contenteditable]');

    if (isInteractive) {
      return;
    }

    if (e.button !== 0) {
      return;
    }

    if (!elementRef.current) return;

    const rect = elementRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });

    setIsDragging(true);
    elementRef.current.style.cursor = 'move';
    elementRef.current.style.userSelect = 'none';
    e.preventDefault();
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    if (!elementRef.current) return;

    const rect = elementRef.current.getBoundingClientRect();
    setResizeOffset({
      x: rect.right - e.clientX,
      y: rect.bottom - e.clientY,
    });

    setResizeEdge('se');
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y,
    };
    setIsResizing(true);
    elementRef.current.style.userSelect = 'none';
    e.stopPropagation();
    e.preventDefault();
  };

  const handleEdgeResizeMouseDown = (edge: string) => (e: React.MouseEvent) => {
    if (!elementRef.current) return;

    setResizeEdge(edge);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y,
    };
    setIsResizing(true);
    elementRef.current.style.userSelect = 'none';
    e.stopPropagation();
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        const constrainedPosition = constrainPosition(newX, newY, size.width, size.height);
        setPosition(constrainedPosition);
      }

      if (isResizing && resizeStartRef.current) {
        const start = resizeStartRef.current;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const edge = resizeEdge;

        let newWidth = start.width;
        let newHeight = start.height;
        let newX = start.posX;
        let newY = start.posY;

        if (edge?.includes('e')) {
          newWidth = Math.max(minSize.width, start.width + dx);
          newWidth = Math.min(newWidth, viewportWidth - newX);
        }
        if (edge?.includes('w')) {
          const proposedWidth = Math.max(minSize.width, start.width - dx);
          const widthDiff = start.width - proposedWidth;
          newX = start.posX + widthDiff;
          newX = Math.max(0, newX);
          newWidth = start.width + (start.posX - newX);
        }
        if (edge?.includes('s')) {
          newHeight = Math.max(minSize.height, start.height + dy);
          newHeight = Math.min(newHeight, viewportHeight - newY);
        }
        if (edge?.includes('n')) {
          const proposedHeight = Math.max(minSize.height, start.height - dy);
          const heightDiff = start.height - proposedHeight;
          newY = start.posY + heightDiff;
          newY = Math.max(0, newY);
          newHeight = start.height + (start.posY - newY);
        }

        setSize({ width: newWidth, height: newHeight });
        setPosition({ x: newX, y: newY });
      }
    },
    [isDragging, isResizing, resizeEdge, dragOffset, position, size, minSize],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    if (elementRef.current) {
      elementRef.current.style.cursor = 'default';
      elementRef.current.style.userSelect = 'auto';
    }
  }, []);

  // Handling window resizing
  useEffect(() => {
    const handleResize = () => {
      const constrainedPosition = constrainPosition(position.x, position.y, size.width, size.height);
      if (constrainedPosition.x !== position.x || constrainedPosition.y !== position.y) {
        setPosition(constrainedPosition);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position, size]);

  // Adding global event handlers
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (elementRef.current) {
      window.addEventListener('mousemove', handleMouseMove, {
        signal,
      });
      window.addEventListener('mouseup', handleMouseUp, {
        signal,
      });
    }

    return () => {
      controller.abort();
    };
  }, [handleMouseMove, handleMouseUp]);

  const style: React.CSSProperties = {
    left: `${position.x}px`,
    top: `${position.y}px`,
    width: `${size.width}px`,
    height: `${size.height}px`,
    minWidth: `${minSize.width}px`,
    minHeight: `${minSize.height}px`,
  };

  return {
    elementRef,
    position,
    size,
    isDragging,
    isResizing,
    handleMouseDown,
    handleResizeMouseDown,
    handleEdgeResizeMouseDown,
    style,
  };
};
